import config from 'constants/config'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { getDestinationImage } from 'lib/search/searchUtils'
import type { SocialImageCandidate } from 'lib/seo/socialImage'
import { defineMessages, type IntlShape } from 'react-intl'

type SearchItemLike = App.SearchItem | undefined

interface Params {
  intl: IntlShape
  vertical?: 'hotels' | 'villas'
  searchItem: SearchItemLike
  destinationId?: string
  destinationName?: string
  totalCount: number
  specificPlaces?: Array<App.SpecificPlace> | undefined
  destinationImageId?: string
}

interface Result {
  title: string
  description: string
  socialImage?: SocialImageCandidate
  imageAlt: string
}

const messages = defineMessages({
  propertyDescriptionWithLocation: {
    id: 'pdr12ZmwV3BFF5AndsvW',
    defaultMessage: 'Discover {propertyName} in {propertyLocation} with {brandName}.',
    description: 'SEO meta description for a property search result page when the property location is available',
  },
  propertyDescriptionWithoutLocation: {
    id: 'DqvsbNdxRR9qgXfc5j5r',
    defaultMessage: 'Discover {propertyName} with {brandName}.',
    description: 'SEO meta description for a property search result page when only the property name is available',
  },
  anywhereTitle: {
    id: 'qPGDE3JLb4Ez_B27wlEr',
    defaultMessage: 'Browse the best hotels on {brandName}',
    description: 'SEO page title for accommodation search results when searching anywhere',
  },
  destinationTitle: {
    id: 'NWA1W56rG88L2k5-vZNQ',
    defaultMessage: 'Browse the best hotels in {locationName}',
    description: 'SEO page title for accommodation search results with a specific destination or location',
  },
  anywhereDescription: {
    id: '1IkUbhNSwgV9Fa6H12lo',
    defaultMessage: 'Explore hotels around the world with {brandName}.',
    description: 'SEO meta description for accommodation search results when searching anywhere',
  },
  destinationDescription: {
    id: 'rExKSGrJzFDfuvvRuy18',
    defaultMessage: 'Explore hotels in {locationName} with {brandName}.',
    description: 'SEO meta description for accommodation search results with a specific destination or location',
  },
  anywhereImageAlt: {
    id: 'H1dTQhLjO06zye41sqHz',
    defaultMessage: '{brandName} hotel search results',
    description: 'Alt text for the SEO image on accommodation search results when searching anywhere',
  },
  destinationImageAlt: {
    id: 'DfU79u1tqDiHLopcc__-',
    defaultMessage: '{locationName} hotels',
    description: 'Alt text for the SEO image on accommodation search results with a specific destination or location',
  },
})

const villaMessages = defineMessages({
  anywhereTitle: {
    id: 'BcAOLolwEUigzW4TdqFp',
    defaultMessage: 'Browse the best homes and villas on {brandName}',
    description: 'SEO page title for home and villa search results when searching anywhere',
  },
  destinationTitle: {
    id: 'v9Ygjy8q73ZgXjyQamMs',
    defaultMessage: 'Browse the best homes and villas in {locationName}',
    description: 'SEO page title for home and villa search results with a specific destination or location',
  },
  anywhereDescription: {
    id: 'tA4dUphfpmxRRkyc0gZr',
    defaultMessage: 'Explore homes and villas around the world with {brandName}.',
    description: 'SEO meta description for home and villa search results when searching anywhere',
  },
  destinationDescription: {
    id: 'h5UbrDghDgmVy7eZkDcm',
    defaultMessage: 'Explore homes and villas in {locationName} with {brandName}.',
    description: 'SEO meta description for home and villa search results with a specific destination or location',
  },
  anywhereImageAlt: {
    id: 'lFn5zsaEdTytgXTvPtOB',
    defaultMessage: '{brandName} home and villa search results',
    description: 'Alt text for the SEO image on home and villa search results when searching anywhere',
  },
  destinationImageAlt: {
    id: 'sl8R8QFZ6nh_8EryNNi7',
    defaultMessage: '{locationName} homes and villas',
    description: 'Alt text for the SEO image on home and villa search results with a specific destination or location',
  },
})

function normaliseSecondaryText(secondaryText?: string): string | undefined {
  if (!secondaryText) {
    return undefined
  }
  return secondaryText
    .split('·')
    .map(part => part.trim())
    .filter(Boolean)
    .join(', ')
}

function findSpecificPlaceImage(places?: Array<App.SpecificPlace>): SocialImageCandidate | undefined {
  if (!places?.length) {
    return undefined
  }

  const candidate = places.find(place => !!place.image)?.image
  if (!candidate) {
    return undefined
  }

  if (candidate.startsWith('http')) {
    return { url: candidate }
  }

  return { id: candidate }
}

function isSearchDestination(searchItem: SearchItemLike): searchItem is App.SearchDestination {
  return searchItem?.searchType === 'destination'
}

function isSearchProperty(searchItem: SearchItemLike): searchItem is App.SearchProperty {
  return searchItem?.searchType === 'property'
}

export function getAccommodationSearchHeadMeta({
  intl,
  vertical = 'hotels',
  searchItem,
  destinationId,
  destinationName,
  specificPlaces,
  destinationImageId,
}: Params): Result {
  const copy = vertical === 'villas' ? villaMessages : messages
  const searchDestination = isSearchDestination(searchItem) ? searchItem : undefined
  const isPropertySearch = isSearchProperty(searchItem)

  const mainText = searchItem?.format?.mainText?.trim()
  const secondaryText = normaliseSecondaryText(searchItem?.format?.secondaryText?.trim())

  const isAnywhereSearch = (destinationId && destinationId === ANYWHERE_PLACE_ID) ||
    (mainText && mainText.toLowerCase() === ANYWHERE_NAME.toLowerCase()) ||
    (!mainText && !destinationName && !isPropertySearch)

  const destinationType = searchDestination?.destinationType

  // Get imageId from searchItem, handling V3 structure
  const searchItemImageId = searchItem?.format?.imageId ||
    (searchItem?.searchType === 'searchItemV3' ? searchItem.display.imageId : searchItem?.imageId)
  const mappedDestinationImageId = destinationId ? getDestinationImage(destinationId) : undefined
  const specificPlaceImage = findSpecificPlaceImage(specificPlaces)
  const preferredImageId = searchItemImageId || destinationImageId
  const socialImage: SocialImageCandidate | undefined = preferredImageId ? { id: preferredImageId }
    : specificPlaceImage ?? (!isAnywhereSearch && mappedDestinationImageId ? { id: mappedDestinationImageId } : undefined)

  if (isPropertySearch) {
    const propertyName = mainText || destinationName || config.title
    const propertyLocation = secondaryText || destinationName
    const propertyDescription = propertyLocation
      ? intl.formatMessage(
          messages.propertyDescriptionWithLocation,
          { propertyName, propertyLocation, brandName: config.title },
        )
      : intl.formatMessage(
          messages.propertyDescriptionWithoutLocation,
          { propertyName, brandName: config.title },
        )

    return {
      title: propertyName,
      description: propertyDescription,
      socialImage,
      imageAlt: propertyName,
    }
  }

  const locationLabel = mainText || destinationName || config.title
  const descriptiveLocation = destinationType === 'country' || !secondaryText || secondaryText.localeCompare(locationLabel, undefined, { sensitivity: 'base' }) === 0
    ? locationLabel
    : `${locationLabel}, ${secondaryText}`
  const description = isAnywhereSearch
    ? intl.formatMessage(copy.anywhereDescription, { brandName: config.title })
    : intl.formatMessage(copy.destinationDescription, {
        locationName: descriptiveLocation,
        brandName: config.title,
      })

  const imageAlt = isAnywhereSearch
    ? intl.formatMessage(copy.anywhereImageAlt, { brandName: config.title })
    : intl.formatMessage(copy.destinationImageAlt, { locationName: descriptiveLocation })

  return {
    title: isAnywhereSearch
      ? intl.formatMessage(copy.anywhereTitle, { brandName: config.title })
      : intl.formatMessage(copy.destinationTitle, { locationName: descriptiveLocation }),
    description,
    socialImage,
    imageAlt,
  }
}
